import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";
import { Button } from "components/ui";
import { DomainScore } from "../../../api/types";
import RadarChart from "apps/user/learner/pages/woneindex/v3/components/radar-chart";
import { useInViews } from "utility/useInViews";
import { trackEvent } from "utility/mixpanel";

import styles from "./stat-cards.module.scss";

interface ScoreBreakdownCardProps {
  domains?: DomainScore[];
  takenAt?: string;
  onExplanationClick?: () => void;
}

const ScoreBreakdownCard: React.FC<ScoreBreakdownCardProps> = ({
  domains: domainsProp,
  takenAt,
  onExplanationClick,
}) => {
  const { ref, inView } = useInViews();
  const location = useLocation();
  const { t } = useTranslation(["components"]);

  const logImpression = useCallback(() => {
    trackEvent("Score Breakdown Card Impression", {
      "Domain Count": domainsProp?.length || 0,
      "Domain Shorthands": domainsProp?.map((d) => d.shorthand) || [],
      "Domain Scores": domainsProp?.map((d) => d.value) || [],
      "Current Page": window.location.href,
      "Current Path": location.pathname,
    });
  }, [domainsProp, location.pathname]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  // Don't render if no data is provided
  if (!takenAt || !domainsProp || domainsProp.length === 0) {
    return null;
  }

  // Transform domain data for recharts with domain names and scores on separate lines
  const chartData = domainsProp.map((domain) => ({
    domain: domain.name,
    score: domain.value || 0,
    fullMark: 100,
  }));

  const handleExplanationClick = () => {
    trackEvent("Index Explanation Clicked", {
      Source: "Score Breakdown Card",
      "Button Label": "How the Index works",
      "Article ID": "58",
      Destination: "/user/articles/58",
    });
    onExplanationClick && onExplanationClick();
  };

  return (
    <div className={styles["index-score-card"]} ref={ref}>
      <div className={styles["index-score-main"]}>
        <RadarChart chartData={chartData} />

        {onExplanationClick && (
          <Button
            variant="enclosed-secondary"
            size="medium"
            monochromeColour="white"
            className={styles["index-score-button"]}
            onClick={handleExplanationClick}
          >
            {t("components:indexScoreCard.howIndexWorks")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ScoreBreakdownCard;
